import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface HeroProps {
  title: string;
  titleSecondary?: string;
  subtitle?: string;
  imageSrc: string;
  imageAlt?: string;
  height?: "small" | "medium" | "large" | "full";
  overlay?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const Hero = ({
  title,
  titleSecondary,
  subtitle,
  imageSrc,
  imageAlt,
  height = "medium",
  overlay = false,
  children,
  className,
}: HeroProps) => {
  const heightClasses = {
    small: "h-[30vh] md:h-[40vh]",
    medium: "h-[50vh] md:h-[60vh]",
    large: "h-[70vh] md:h-[80vh]",
    full: "h-screen",
  };

  return (
    <div
      className={cn(
        "relative w-full flex items-center justify-center overflow-hidden",
        heightClasses[height],
        className
      )}
    >
      <div className="absolute inset-0 w-full h-full">
        <motion.img
          src={imageSrc}
          alt={imageAlt || title}
          className="w-full h-full object-cover object-center"
          initial={{ scale: 1.05, opacity: 0.8 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, ease: [0.6, 0.05, 0.01, 0.99] }}
        />
      </div>

      

      <div className="relative z-10 text-center px-6 max-w-4xl">
          <motion.h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold mb-2 text-white [text-shadow:_-1px_-1px_0_rgba(0,0,0,0.5),_1px_-1px_0_rgba(0,0,0,0.5),_-1px_1px_0_rgba(0,0,0,0.5),_1px_1px_0_rgba(0,0,0,0.5)]"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {title}
          </motion.h1>

          {titleSecondary && (
            <motion.p
              className="text-xl md:text-2xl lg:text-3xl font-light mb-4 text-white/90 italic [text-shadow:_-1px_-1px_0_rgba(0,0,0,0.5),_1px_-1px_0_rgba(0,0,0,0.5),_-1px_1px_0_rgba(0,0,0,0.5),_1px_1px_0_rgba(0,0,0,0.5)]"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              {titleSecondary}
            </motion.p>
          )}

          {subtitle && (
            <motion.p
              className="text-sm sm:text-base md:text-xl font-light mb-8 text-white shadow-[0_0_3px_rgba(0,0,0,0.8)] bg-black/20 backdrop-blur-sm py-1 px-3 rounded-md inline-block"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {subtitle}
            </motion.p>
          )}

          {children && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {children}
            </motion.div>
          )}
      </div>
    </div>
  );
};

export default Hero;
